import React from "react";
import { useTranslation } from "react-i18next";
import styles from "./error-fallback.module.scss";
import { tokens } from "../../../styles/tokens";

interface ErrorFallbackProps {
  error?: Error;
  resetError?: () => void;
  title?: string;
  message?: string;
  showDetails?: boolean;
  showReload?: boolean;
  showHome?: boolean;
  /** When true, display a friendly 404 page instead of a technical error */
  showAsNotFound?: boolean;
}

const GlobeSvg: React.FC = () => (
  <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="60" cy="60" r="56" stroke={tokens.indigo600} strokeWidth="3" fill={tokens.indigo50} />
    <ellipse cx="60" cy="60" rx="28" ry="56" stroke={tokens.indigo600} strokeWidth="2" fill="none" />
    <path d="M4 60 h112" stroke={tokens.indigo600} strokeWidth="2" />
    <path d="M12 35 h96" stroke={tokens.indigo600} strokeWidth="1.5" opacity="0.6" />
    <path d="M12 85 h96" stroke={tokens.indigo600} strokeWidth="1.5" opacity="0.6" />
    <text x="60" y="67" textAnchor="middle" fontSize="28" fontWeight="700" fill={tokens.indigo600}>?</text>
  </svg>
);

const ErrorIconSvg: React.FC = () => (
  <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="60" cy="60" r="56" stroke="currentColor" strokeWidth="3" fill={tokens.errBg} />
    <path
      d="M60 35v30"
      stroke="currentColor"
      strokeWidth="6"
      strokeLinecap="round"
    />
    <circle cx="60" cy="80" r="4" fill="currentColor" />
  </svg>
);

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetError,
  title,
  message,
  showDetails = false,
  showReload = true,
  showHome = true,
  showAsNotFound = false,
}) => {
  const { t } = useTranslation();

  const handleReload = () => {
    window.location.reload();
  };

  if (showAsNotFound) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.card}>
          <div className={styles.illustration}>
            <span className={styles.code}>4</span>
            <div className={styles.globe}>
              <GlobeSvg />
            </div>
            <span className={styles.code}>4</span>
          </div>

          <h1 className={styles.title}>
            {title || t("error.notFound.title", "Page Not Found")}
          </h1>
          <p className={styles.subtitle}>
            {message || t("error.notFound.description", "Looks like this page went on vacation. Let's get you back on track!")}
          </p>

          <div className={styles.actions}>
            {showHome && (
              // Real anchor (not a button) so crawlers see a followable link home.
              <a
                href="/"
                className={`${styles.button} ${styles.primary}`}
              >
                {t("error.goHome", "Go Home")}
              </a>
            )}
            {showReload && (
              <button
                onClick={handleReload}
                className={`${styles.button} ${styles.secondary}`}
              >
                {t("error.reload", "Reload Page")}
              </button>
            )}
          </div>

          {/* Crawlable links to key public pages so this page is not a dead end */}
          <nav className={styles.notFoundLinks} aria-label={t("error.notFound.suggestionsLabel", "Helpful links")}>
            <a href="/courses" className={styles.notFoundLink}>
              {t("error.notFound.toCourses", "German courses")}
            </a>
            <a href="/exercises" className={styles.notFoundLink}>
              {t("error.notFound.toExercises", "German exercises")}
            </a>
          </nav>
        </div>
      </div>
    );
  }

  const displayTitle = title || t("error.title", "Something went wrong");
  const displayMessage =
    message ||
    error?.message ||
    t("error.message", "An unexpected error occurred. Please try again.");

  return (
    <div className={styles.wrapper}>
      <div className={styles.card}>
        <div className={styles.iconContainer}>
          <ErrorIconSvg />
        </div>

        <h1 className={styles.title}>{displayTitle}</h1>
        <p className={styles.subtitle}>{displayMessage}</p>

        <div className={styles.actions}>
          {resetError && (
            <button
              onClick={resetError}
              className={`${styles.button} ${styles.primary}`}
            >
              {t("error.tryAgain", "Try Again")}
            </button>
          )}

          {showReload && (
            <button
              onClick={handleReload}
              className={`${styles.button} ${styles.secondary}`}
            >
              {t("error.reload", "Reload Page")}
            </button>
          )}

          {showHome && (
            <a
              href="/"
              className={`${styles.button} ${styles.secondary}`}
            >
              {t("error.goHome", "Go Home")}
            </a>
          )}
        </div>

        {showDetails && error && (
          <details className={styles.errorDetails}>
            <summary className={styles.detailsToggle}>
              {t("error.technicalDetails", "Technical Details")}
            </summary>
            <div className={styles.errorContent}>
              <h4>{t("error.errorMessage", "Error Message")}:</h4>
              <pre className={styles.errorText}>{error.message}</pre>

              {error.stack && (
                <>
                  <h4>{t("error.stackTrace", "Stack Trace")}:</h4>
                  <pre className={styles.stackTrace}>{error.stack}</pre>
                </>
              )}
            </div>
          </details>
        )}
      </div>
    </div>
  );
};

export default ErrorFallback;
